import { BaseSelectorOption } from "@/types";
import {
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  SelectChangeEvent,
  SxProps
} from "@mui/material";
import { Theme } from "klayowebshared";
import React, { Component } from "react";
import { withLocale, WithLocaleProps } from "../providers/LocaleProvider";
import SelectedIcon from "../resources/images/icons-check.svg";
import DropIcon from "../resources/images/icons-chevron-double.svg";
import { TextField } from "./TextField";

interface MenuSelectProps<
  TOption extends BaseSelectorOption = BaseSelectorOption,
  TValue = TOption | string
> extends WithLocaleProps {
  noSelectionError?: boolean;
  groups?: string[];
  highlightSelection?: boolean;
  isAutoFocus?: boolean;
  dense?: boolean;
  getItemLabel?: (item: TOption) => string;
  disableItem?: (item: TOption) => boolean;
  clearable?: boolean;
  value?: TValue | null;
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<TValue>
  ) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  firstValueDefault?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  menuClassName?: string;
  sx?: SxProps;
  CustomHelperText?: string;
  label?: string | null;
  items?: TOption[];
  defaultValue?: TValue | null;
}

interface MenuSelectState {
  isTouched: boolean;
  width: number;
}

class MenuSelectBase<
  TOption extends BaseSelectorOption = BaseSelectorOption,
  TValue = TOption | string
> extends Component<MenuSelectProps<TOption, TValue>, MenuSelectState> {
  static defaultProps = {
    noSelectionError: false,
    groups: undefined,
    highlightSelection: true
  };

  ref: any;

  constructor(props: MenuSelectProps<TOption, TValue>) {
    super(props);

    this.state = {
      isTouched: false,
      width: 0
    };

    this.ref = React.createRef();
  }

  componentDidMount() {
    const { isAutoFocus } = this.props;
    if (isAutoFocus) this.setState({ isTouched: true });
  }

  onChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<TValue>
  ) {
    const { onChange } = this.props;
    if (e.target.value === undefined) return;

    if (onChange) onChange(e);

    this.setState({ isTouched: true });
  }

  onBlur(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
    this.setState({ isTouched: true });
  }

  renderGroup(group: string) {
    const { items } = this.props;
    return [
      <ListSubheader
        key={Math.random().toString(36).substring(7)}
        className='klayo-selectmenu_group'
      >
        {group}
      </ListSubheader>,
      items
        ?.filter(([key, filter]: any) => filter.group === group)
        .map(([key, filter]: any) => this.renderItem(filter))
    ];
  }

  getHelperText() {
    const { CustomHelperText, noSelectionError, value, translate } = this.props;
    const { isTouched } = this.state;
    let textContent = "";

    if (noSelectionError && value === null && isTouched) {
      textContent = CustomHelperText
        ? CustomHelperText
        : translate
          ? translate("placeholders.pleaseSelectOption")
          : "Please select an option";
    }

    return textContent;
  }

  onFocus(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const width = e.target.offsetWidth;
    this.setState({ width });
  }

  renderItem(filter: any) {
    const { dense, getItemLabel, disableItem, highlightSelection, value } = this.props;

    return (
      <MenuItem
        className={"klayo-selectmenu_item " + (dense ? "klayo-selectmenu_item-dense " : "")}
        key={filter?.value || filter?.id}
        selected={value === filter?.value}
        disabled={disableItem ? disableItem(filter) : false}
        value={filter?.value || filter?.id}
      >
        <ListItemText>{getItemLabel ? getItemLabel(filter) : filter.label}</ListItemText>
        {highlightSelection && (
          <ListItemIcon>
            <SelectedIcon />
          </ListItemIcon>
        )}
      </MenuItem>
    );
  }

  render() {
    const {
      label,
      items,
      placeholder,
      disabled,
      className,
      menuClassName,
      sx,
      dense,
      onChange,
      firstValueDefault,
      getItemLabel,
      clearable,
      disableItem,
      noSelectionError,
      groups
    } = this.props;
    const { isTouched } = this.state;
    let { value } = this.props;

    const defaultValue = this.props.defaultValue
      ? this.props.defaultValue
      : firstValueDefault && items
        ? items[0][1]
        : null;
    if (!value) value = defaultValue;

    const helperText = this.getHelperText();

    const find = items?.find((i: any) => {
      return i?.value === value;
    });

    return (
      <TextField
        className={
          "klayo-selectmenu" +
          (dense ? " klayo-selectmenu-dense" : "") +
          (className ? " " + className : "") +
          (groups ? " klayo-selectmenu-hasgroups" : "")
        }
        select
        label={label}
        dense={dense}
        value={value}
        error={noSelectionError && value === null && isTouched}
        helperText={helperText}
        clearable={clearable && !disabled}
        defaultValue={defaultValue}
        placeholder={placeholder}
        disabled={disabled}
        onChange={this.onChange.bind(this)}
        onBlur={this.onBlur.bind(this)}
        SelectProps={{
          MenuProps: {
            classes: {
              list:
                ".MuiMenu-list" +
                (menuClassName ? ` ${menuClassName} ` : "") +
                (groups ? " klayo-selectmenu_list-hasgroups" : "") +
                (dense ? " klayo-selectmenu_list-dense" : "")
            }
          },
          IconComponent: () => <DropIcon className='klayo-selectmenu_dropbutton' />,
          autoWidth: false,
          displayEmpty: true,
          renderValue: (value: any) => (
            <div className='klayo-selectmenu_value'>
              {value !== "" ? (
                getItemLabel ? (
                  getItemLabel(value)
                ) : (
                  (find?.label ?? value.label)
                )
              ) : placeholder ? (
                <div className='klayo-selectmenu_placeholder'>{placeholder}</div>
              ) : (
                ""
              )}
            </div>
          )
        }}
        sx={{
          ...sx,
          "& fieldset legend span": {
            display: "none"
          },
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: Theme.getStyleVar("--neon-blue-nb-200")
            },
            "&:hover fieldset": {
              borderColor: Theme.getStyleVar("--neon-blue-nb-300")
            },
            "&.Mui-focused fieldset": {
              borderWidth: "2px",
              borderColor: Theme.getStyleVar("--neon-blue-nb-500")
            }
          }
        }}
        onFocus={this.onFocus.bind(this)}
      >
        {groups && groups.map((g) => this.renderGroup(g))}

        {!groups && items && items.map((filter) => this.renderItem(filter))}
      </TextField>
    );
  }
}

// export const MenuSelect = withLocale(MenuSelectBase);
export const MenuSelect = withLocale(MenuSelectBase) as <
  T extends BaseSelectorOption = BaseSelectorOption,
  TValue = T | string
>(
  props: Omit<MenuSelectProps<T, TValue>, keyof WithLocaleProps> &
    React.RefAttributes<MenuSelectBase<T, TValue>>
) => React.ReactElement;
